import React, { useEffect, useLayoutEffect, useState } from 'react';
import PageHeader from '../molecules/PageHeader';
import trackService, { AUDITEVENTTYPE } from 'services/trackService';
import { sendCustomEvent } from 'common/utility';
import withAdasMemberData from 'hoc/withAdasMemberData';
import { Stack } from '@mui/material';
import CareManagementLettersGrid from './CareManagementLettersGrid';
import ReferralNotificationLettersGrid from './ReferralNotificationLettersGrid';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { useLocation } from 'react-router-dom';
import Post from 'components/molecules/Post/Post';

const CM_UM_NOTIFICATIONS_FLAG = 'cm-um-notifications';

export type HealthSummaryProps = {
    member: any;
};

const HealthSummary = (props: HealthSummaryProps) => {
    const { hash } = useLocation();
    const { member } = props;
    const { isFeatureFlagLoading, enabled } = useFeatureFlag(CM_UM_NOTIFICATIONS_FLAG);
    const [loadMap, setLoadMap] = useState({
        referrals: false,
        careManagement: false,
    });
    function handleLoad(topic: string) {
        setLoadMap((latest) => ({ ...latest, [topic]: true }));
    }
    useEffect(() => {
        const trackPageVisitSub = trackService
            .customEvent$({ eventType: AUDITEVENTTYPE.MemberPortalViewHealthSummary })
            .subscribe();

        return () => {
            trackPageVisitSub.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!member?.isSNP) {
            setLoadMap((latest) => ({ ...latest, careManagement: true }));
        }
    }, [member?.isSNP]);

    useLayoutEffect(() => {
        if (hash && Object.values(loadMap).every(Boolean)) {
            const element = document.querySelector(hash);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [hash, loadMap]);

    const posts = [
        {
            icon: 'doctor',
            header: `Get Virtual Care`,
            subHeader: `Talk to your doctor by phone or video`,
            to: '/virtual-care',
        },
        {
            icon: 'microscope',
            header: 'View Quest Results',
            subHeader: 'Make an appointment or see lab results',
            href: 'https://myquest.questdiagnostics.com/web/home',
            target: '_blank',
            onClick: () => {
                sendCustomEvent({ eventType: AUDITEVENTTYPE.MemberPortalViewQuest });
            },
        },
        {
            icon: 'test-tube',
            header: `View LabCorp Results`,
            subHeader: `Make an appointment or see lab results`,
            href: 'https://patient.labcorp.com',
            target: '_blank',
            onClick: () => {
                sendCustomEvent({ eventType: AUDITEVENTTYPE.MemberPortalViewLabCorp });
            },
        },
    ];

    return (
        <section>
            <PageHeader>
                <span className="bold-text">My Health Summary</span>
            </PageHeader>
            <p>
                You can share this overview of your health with your doctor. This information is
                also handy when you go to a hospital or other treatment center. Just click on
                the topic title below to see more information or complete your health
                assessment.
            </p>
            <Stack direction={{
                xs: "column",
                md: "row",
            }} justifyContent="center" spacing={2}>
                {posts.map((post, i) => (
                    <Stack flex={1} key={i}>
                        <Post {...post} />
                    </Stack>
                ))}
            </Stack>
            {!isFeatureFlagLoading && enabled && (
                <>
                    <CareManagementLettersGrid
                        member={member}
                        onLoad={handleLoad.bind({}, 'careManagement')}
                    />
                    <ReferralNotificationLettersGrid
                        onLoad={handleLoad.bind({}, 'referrals')}
                    />
                </>
            )}
        </section>
    );
};

export default withAdasMemberData(HealthSummary);
